import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EventCard from '@/components/EventCard';
import { useAuth } from '@/contexts/auth-context';
import { fetchUserProfileById, fetchEventsByOrganizer, fetchOrganizerOwnEventsPublic, fetchOrganizerById, addAuthorizationEmail, updateAuthorizationEmail, removeAuthorizationEmail, followOrganizer, unfollowOrganizer } from '@/apiEndpoints';
import NoEvent from '@/icons/EmptyState/NoEvent';
import Twitter from '@/icons/Twitter';
import Linkedin from '@/icons/Linkedin';
import Instagram from '@/icons/Instagram';
import Facebook from '@/icons/Facebook';
import Follow from '@/icons/Follow';
import Location from '@/icons/Location';
import moment from 'moment';
import Avatar from '@/components/Avatar';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconMapPin as MapPin, IconPhone as Phone, IconMail as Mail } from '@tabler/icons-react';
import { wrap, pageBg, glow, eyebrow, btnGhost, btnAccent, Breadcrumbs, EmptyState, Segmented } from '@/components/ui/design';

// Helper function to get site name from environment
const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

// Organizer address is stored as a single formatted string (e.g. "1882 Cochran Ave, Los Angeles, CA 90019, USA").
// There's no separate city field, so pull just the city out for display.
const getCityFromAddress = (address?: string): string => {
    if (!address) return '';
    const parts = address.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 4) return parts[parts.length - 3]; // street, city, state zip, country
    if (parts.length >= 2) return parts[0]; // city, state[, country]
    return parts[0] || address;
};

// Mock host data
type Social = { icon: string; url: string };
type Host = {
    id: string;
    name: string;
    location: string;
    avatar: string;
    followers: number;
    events: number;
    about: string[];
    socials: Social[];
};
// Add isPast property to mockEvents for tab filtering
type EventCard = {
    id: string;
    title: string;
    date: string;
    location: string;
    image: string;
    host: Host | { name: string };
    price: string;
    likes: number;
    isNew: boolean;
    isPast?: boolean;
};

export default function HostProfile() {
    const { id } = useParams();
    const { user, isAuthenticated } = useAuth();
    const [tab, setTab] = useState<'Upcoming' | 'Past'>('Upcoming');
    const [isFollowing, setIsFollowing] = useState(false);
    const [showAllAbout, setShowAllAbout] = useState(false);
    const [isAboutOverflowing, setIsAboutOverflowing] = useState(false);
    const aboutRef = useRef<HTMLDivElement>(null);
    const [showAuthEmails, setShowAuthEmails] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newRole, setNewRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const [editingEmail, setEditingEmail] = useState<string | null>(null);
    const [editingRole, setEditingRole] = useState<'admin' | 'eventmanager' | 'usher'>('usher');
    const queryClient = useQueryClient();

    // Fetch real host and events data
    const { data: hostArr, isLoading: isHostLoading, refetch } = useQuery({
        queryKey: ['host', id],
        queryFn: () => fetchOrganizerById(id as string),
        enabled: !!id,
    });
    const host = Array.isArray(hostArr) ? hostArr[0] : hostArr;
    const { data: events = [], isLoading: isEventsLoading } = useQuery({
        queryKey: ['hostEvents', id],
        queryFn: () => fetchOrganizerOwnEventsPublic(id as string),
        enabled: !!id,
    });
    useEffect(() => {
        setIsFollowing(host?.isFollowed || false);
    }, [host]);
    // Detect whether the (clamped) about text actually overflows 3 lines, so the
    // More/Less toggle also shows up for one long paragraph without line breaks.
    useEffect(() => {
        setShowAllAbout(false);
        if (aboutRef.current) {
            setIsAboutOverflowing(aboutRef.current.scrollHeight > aboutRef.current.clientHeight + 1);
        } else {
            setIsAboutOverflowing(false);
        }
    }, [host?.about]);
    // Follow/Unfollow mutation
    const followMutation = useMutation({
        mutationFn: async (shouldFollow: boolean) => {
            if (!id) throw new Error('Organizer ID is required');
            if (shouldFollow) {
                return await followOrganizer(id);
            } else {
                return await unfollowOrganizer(id);
            }
        },
        onSuccess: (data, shouldFollow) => {
            refetch()
            setIsFollowing(shouldFollow);
            // Update the follow count in the cache
        },
        onError: (error) => {
            console.error('Follow/unfollow error:', error);
        },
    });

    // Authorization email mutations
    const addEmailMutation = useMutation({
        mutationFn: () => addAuthorizationEmail(id as string, newEmail, newRole),
        onSuccess: () => {
            setNewEmail('');
            setNewRole('usher');
            queryClient.invalidateQueries({ queryKey: ['host', id] });
        },
    });

    const updateEmailMutation = useMutation({
        mutationFn: () => updateAuthorizationEmail(id as string, editingEmail!, editingRole),
        onSuccess: () => {
            setEditingEmail(null);
            setEditingRole('usher');
            queryClient.invalidateQueries({ queryKey: ['host', id] });
        },
    });

    const removeEmailMutation = useMutation({
        mutationFn: (email: string) => removeAuthorizationEmail(id as string, email),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['host', id] });
        },
    });

    // Filter events by isPast (if available)
    const filteredEvents = Array.isArray(events?.data)
        ? events?.data?.filter((e: any) => (tab === 'Upcoming' ? moment(e.times?.[0]?.startDate).isAfter(moment()) : moment(e.times?.[0]?.startDate).isBefore(moment())))
            .sort((a: any, b: any) => {
                const diff = moment(a.times?.[0]?.startDate).valueOf() - moment(b.times?.[0]?.startDate).valueOf();
                return tab === 'Upcoming' ? diff : -diff;
            })
        : [];

    if (isHostLoading) {
        return (
            <>
                <Header />
                <div className={`${pageBg} w-full min-h-screen flex flex-col overflow-x-clip`} style={{ overscrollBehavior: 'none' }}>
                    <div className={`${wrap} pt-6 sm:pt-10`}>
                        <div className="w-full h-56 md:h-80 bg-grey95 dark:bg-white/[0.06] rounded-[28px] animate-pulse" />
                        <div className="pt-8 pb-16 flex flex-col lg:flex-row gap-8 lg:gap-12 w-full">
                            <aside className="w-full lg:w-80 flex-shrink-0">
                                <div className="rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-6 flex flex-col items-start animate-pulse">
                                    <div className="w-[88px] h-[88px] rounded-full bg-grey95 dark:bg-white/10 mb-4" />
                                    <div className="h-7 bg-grey95 dark:bg-white/10 rounded-full w-40 mb-4" />
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-40 mb-3" />
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-28 mb-3" />
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-36 mb-5" />
                                    <div className="h-12 bg-grey95 dark:bg-white/10 rounded-full w-full mb-4" />
                                    <div className="h-16 bg-grey95 dark:bg-white/10 rounded-2xl w-full mb-4" />
                                    <div className="flex gap-2 mb-4 w-full justify-center">
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} className="w-10 h-10 rounded-full bg-grey95 dark:bg-white/10" />
                                        ))}
                                    </div>
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-full mb-2" />
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-full mb-2" />
                                    <div className="h-4 bg-grey95 dark:bg-white/10 rounded-full w-2/3" />
                                </div>
                            </aside>
                            <main className="flex-1 min-w-0">
                                <div className="h-14 w-48 bg-grey95 dark:bg-white/[0.06] rounded-2xl animate-pulse mb-6" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {[1, 2, 3, 4, 5, 6].map(i => (
                                        <div key={i} className="animate-pulse">
                                            <div className="w-full aspect-[4/5] bg-grey95 dark:bg-white/[0.06] rounded-[28px] mb-4" />
                                            <div className="h-4 bg-grey95 dark:bg-white/[0.06] rounded-full w-3/4 mb-2" />
                                            <div className="h-4 bg-grey95 dark:bg-white/[0.06] rounded-full w-1/2" />
                                        </div>
                                    ))}
                                </div>
                            </main>
                        </div>
                    </div>
                </div>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{host?.orgName ? `${host.orgName} - ${getSiteName()}` : `Organization - ${getSiteName()}`}</title>
                <meta name="description" content={host?.about ? (Array.isArray(host.about) ? host.about[0] : host.about) : `Discover events organized by ${host?.orgName || 'this organization'} on ${getSiteName()}.`} />
                <meta name="keywords" content={`${host?.orgName}, events, organizer, ${host?.address}, ${getSiteName()}`} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="profile" />
                <meta property="og:title" content={host?.orgName ? `${host.orgName} - ${getSiteName()}` : `Organization - ${getSiteName()}`} />
                <meta property="og:description" content={host?.about ? (Array.isArray(host.about) ? host.about[0] : host.about) : `Discover events organized by ${host?.orgName || 'this organization'} on ${getSiteName()}.`} />
                <meta property="og:image" content={host?.attachments?.avatar?.link || '/logo.png'} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content={getSiteName()} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={host?.orgName ? `${host.orgName} - ${getSiteName()}` : `Organization - ${getSiteName()}`} />
                <meta name="twitter:description" content={host?.about ? (Array.isArray(host.about) ? host.about[0] : host.about) : `Discover events organized by ${host?.orgName || 'this organization'} on ${getSiteName()}.`} />
                <meta name="twitter:image" content={host?.attachments?.avatar?.link || '/logo.png'} />

                {/* Additional meta tags */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content={host?.orgName || getSiteName()} />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <div className={`${pageBg} relative isolate w-full min-h-screen flex flex-col overflow-x-clip`} style={{ overscrollBehavior: 'none' }}>
                <div className={glow} />
                <div className={`${wrap} pt-6 sm:pt-10`}>
                <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Organizers', to: '/search?browse=organizers' }, { label: host?.orgName || 'Organization' }]} />

                {host?.attachments?.banner?.link ? (
                    <div className="mt-6 w-full h-56 md:h-80 bg-[#0a0a0a] flex items-center justify-center relative overflow-hidden rounded-[28px] ring-1 ring-black/[0.06] dark:ring-white/10">
                        <img
                            src={host?.attachments?.banner?.link}
                            alt="Banner"
                            className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-500"
                        />
                    </div>
                ) : (
                    null
                )}

                <div className="pt-8 pb-16 flex flex-col lg:flex-row gap-8 lg:gap-12 w-full">
                    <aside className="w-full lg:w-80 flex-shrink-0">
                        <div className="rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-6 flex flex-col items-start lg:sticky lg:top-24">
                            <Avatar
                                src={host?.attachments?.avatar?.link}
                                name={host?.orgName}
                                seed={id}
                                variant="org"
                                size={88}
                                className="ring-4 ring-white dark:ring-nightBg mb-5"
                            />
                            <div className="text-[clamp(28px,4vw,36px)] font-bold leading-[1] tracking-[-0.04em] mb-5 text-grey10 dark:text-white break-words max-w-full">{host?.orgName}</div>
                            <div className="text-grey40 dark:text-nightMuted text-sm mb-3 flex items-center gap-2">
                                <MapPin className="h-4 w-4 flex-shrink-0" />
                                {getCityFromAddress(host?.address)}</div>
                            <a href={`tel:${host?.phone}`} className="text-grey10 hover:text-primary dark:text-white dark:hover:text-nightAccent text-sm font-medium mb-3 flex items-center gap-2 transition-colors">
                                <Phone className="h-4 w-4 flex-shrink-0 text-grey40 dark:text-nightMuted" />
                                {host?.phone}</a>
                            <a href={`mailto:${host?.email}`} className="text-grey10 hover:text-primary dark:text-white dark:hover:text-nightAccent text-sm font-medium mb-5 flex items-center gap-2 min-w-0 max-w-full break-all transition-colors">
                                <Mail className="h-4 w-4 flex-shrink-0 text-grey40 dark:text-nightMuted" />
                                {host?.email}</a>
                            {isAuthenticated && (
                                <button
                                    className={`${isFollowing ? btnGhost : btnAccent} mb-4 w-full`}
                                    onClick={() => followMutation.mutate(!isFollowing)}
                                    disabled={followMutation.isPending}
                                >
                                    <div className='flex gap-2 items-center justify-center'>
                                        <Follow />
                                        {followMutation.isPending ? (isFollowing ? 'Unfollowing...' : 'Following...') : isFollowing ? 'Following' : 'Follow'}
                                    </div>
                                </button>
                            )}
                            <div className="grid grid-cols-2 gap-2 mb-5 w-full">
                                <span className='flex flex-col items-start rounded-2xl bg-white px-4 py-3 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10'><span className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{host?.followCount}</span> <span className="text-xs font-semibold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Followers</span></span>
                                <span className='flex flex-col items-start rounded-2xl bg-white px-4 py-3 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10'><span className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{events?.data?.length || 0}</span> <span className="text-xs font-semibold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Events</span></span>
                            </div>
                            {host?.instagram || host?.twitter || host?.linkedin || host?.facebook ? (
                                <div className="flex gap-2 mb-5 w-full">
                                    {host?.instagram && (
                                        <a
                                            href={host.instagram.startsWith('http') ? host.instagram : `https://instagram.com/${host.instagram}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                        >
                                            <Instagram />
                                        </a>
                                    )}
                                    {host?.twitter && (
                                        <a
                                            href={host.twitter.startsWith('http') ? host.twitter : `https://twitter.com/${host.twitter}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                        >
                                            <Twitter />
                                        </a>
                                    )}
                                    {host?.linkedin && (
                                        <a
                                            href={host.linkedin.startsWith('http') ? host.linkedin : `https://linkedin.com/in/${host.linkedin}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                        >
                                            <Linkedin />
                                        </a>
                                    )}
                                    {host?.facebook && (
                                        <a
                                            href={host.facebook.startsWith('http') ? host.facebook : `https://facebook.com/${host.facebook}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10"
                                        >
                                            <Facebook />
                                        </a>
                                    )}
                                </div>
                            ) : null}
                            {(() => {
                                const aboutText = Array.isArray(host?.about) ? host.about.join('\n') : (host?.about || '');
                                if (!aboutText) return null;
                                return (
                                    <div className="text-left mb-2 w-full">
                                        <div
                                            ref={aboutRef}
                                            className={`text-sm leading-relaxed text-grey40 dark:text-nightMuted whitespace-pre-line ${showAllAbout ? '' : 'line-clamp-3'}`}
                                        >
                                            {aboutText}
                                        </div>
                                        {isAboutOverflowing && (
                                            <button
                                                type="button"
                                                className="mt-1 text-grey10 hover:text-primary dark:text-white dark:hover:text-nightAccent font-semibold text-left text-sm transition-colors"
                                                onClick={() => setShowAllAbout(v => !v)}
                                            >
                                                {showAllAbout ? 'Less' : 'More...'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}

                        </div>
                    </aside>
                    <main className="flex-1 min-w-0">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <p className={eyebrow}>Events</p>
                                <h2 className="mt-3 text-[clamp(36px,5.5vw,72px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white">{tab}.</h2>
                            </div>
                            <Segmented
                                label="Events"
                                options={[{ key: 'Upcoming', label: 'Upcoming' }, { key: 'Past', label: 'Past' }]}
                                value={tab}
                                onChange={(key) => setTab(key as 'Upcoming' | 'Past')}
                            />
                        </div>
                        {filteredEvents.length > 0 ?
                            <div className="mt-4 grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 xl:grid-cols-3">
                                <>
                                    {filteredEvents.map((event: any, i: number) => (
                                        <EventCard key={i} event={event} userInfo={null} organizerInfo={host} />
                                    ))}
                                </>
                            </div>
                            :
                            <div className="mt-8">
                                <EmptyState title={<span className="flex flex-col items-center"><NoEvent /><span className="mt-2">There is no event</span></span>} />
                            </div>
                        }
                    </main>
                </div>
                </div>
            </div>
            <Footer hasMobileNav />
            <MobileBottomNav />
        </>
    );
} 